import { injectable, inject } from 'inversify'
import { action, computed, observable, makeObservable, runInAction } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { ActiveGamesSource, ActiveGameEntryVm } from '@core/interfaces/active-games-source'
import { HttpGateway } from '@core/services/http.gateway'
import type { ClockGateway } from '@core/services/clock.gateway'
import type { ActionFailurePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { UserModel } from '@core/infra/authentication/user.model'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationOwner } from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { activeGameSummarySchema, type GameStatus, type Seat } from '@shared/index'

export type ActiveGamePm = {
  readonly gameId: string
  readonly status: GameStatus
  readonly hostName: string
  readonly guestName: string | null
  readonly yourSeat: Seat
  readonly activeSeat: Seat | null
}

// Your own move/command/strike/draw already forces an immediate check
// (`GameRepository`'s own turn-action `succeeded` callbacks) — the interval
// below is only ever what stands between the *opponent's* move landing and
// the sidebar noticing it, which has no equivalent hook to shortcut. Left at
// the same order of magnitude as `PresenceRepository`'s own poll (slower
// than the lobby's or the game page's own 3s poll, both per-page rather
// than app-wide): a shorter interval here starts colliding with other
// tests' own unrelated clock-advance amounts across the whole suite.
const ACTIVE_GAMES_POLL_INTERVAL_MS = 10_000

type ActiveGamesCheckFailure = ActionFailurePm

export const ACTIVE_GAMES_CHECK_OPERATION = defineOperation<null, ActiveGamesCheckFailure>({
  id: operationId('game.active-games-check'),
  key: () => operationKey('current'),
  groups: () => [operationGroup('session'), operationGroup('game')],
  pending: { kind: 'ignore' },
  safety: { kind: 'read' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * Every non-finished game the signed-in account holds a seat in — feeds the
 * sidebar's "Play" section (`NavigationPresenter`) with one live entry per
 * open game. Polled, same reasoning as `PresenceRepository`/`InboxRepository`:
 * no websocket/SSE infrastructure exists in this app, so a few seconds of
 * staleness is the accepted trade. Deliberately separate from `GameRepository`
 * (single current game, session-scoped to one page) — this one runs for the
 * whole app, on every route, the moment a session begins.
 */
@injectable()
export class ActiveGamesRepository implements AppLifecycle, UserScopedState, ActiveGamesSource {
  gamesPm: readonly ActiveGamePm[] = []

  @inject(Types.IClockGateway)
  private clockGateway!: ClockGateway

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(UserModel)
  private userModel!: UserModel

  private readonly dataGateway: HttpGateway
  private readonly operationOwner: OperationOwner
  private readonly checkOperation: OperationHandle<null, ActiveGamesCheckFailure>

  private pollingStarted = false

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.operationOwner = operationRepository.createOwner({ id: 'active-games.repository', lifetime: 'session' })
    this.checkOperation = this.operationOwner.register(ACTIVE_GAMES_CHECK_OPERATION)

    makeObservable(this, {
      gamesPm: observable.ref,
      entries: computed,
      checkNow: action,
    })
  }

  /**
   * `ActiveGamesSource`'s own contract (`core/interfaces/active-games-
   * source.ts`) — fully resolved (translated label, decided route) so the
   * consumer (`NavigationPresenter`, under `core/`) never has to know what a
   * "game" is, only how to render a menu row.
   */
  get entries(): readonly ActiveGameEntryVm[] {
    const t = this.i18nRepository.t.bind(this.i18nRepository)
    return this.gamesPm.map((game) => {
      const opponentName = (game.yourSeat === 'host' ? game.guestName : game.hostName) ?? ''
      const statusWord =
        game.status !== 'playing'
          ? t('nav.activeGame.waiting')
          : game.activeSeat === game.yourSeat
            ? t('nav.activeGame.yourTurn')
            : t('nav.activeGame.opponentTurn')
      return {
        key: `game:${game.gameId}`,
        label: t('nav.activeGame', { opponent: opponentName, status: statusWord }),
        routesTo: game.status === 'playing' || game.status === 'finished' ? 'Game.Play' : 'Game.Setup',
        params: { gameId: game.gameId },
      }
    })
  }

  initialize(): void {
    if (this.pollingStarted) return
    this.pollingStarted = true

    void this.checkNow()
    this.clockGateway.everyInterval(ACTIVE_GAMES_POLL_INTERVAL_MS, () => {
      void this.checkNow()
    })
  }

  checkNow = async (): Promise<void> => {
    // Nothing to poll before a session exists — this runs from the moment
    // the app boots (AppLifecycle), including on the login page, and /games
    // is an authenticated endpoint. Without this, every tick 401s, which
    // HttpGateway turns into a refresh attempt that then fails and raises
    // "session expired" for someone who was never signed in to begin with.
    if (!this.userModel.token) return

    await this.checkOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/games', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = activeGameSummarySchema.array().safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (games) => {
          this.gamesPm = games.map((game) =>
            Object.freeze({
              gameId: game.gameId,
              status: game.status,
              hostName: game.hostName,
              guestName: game.guestName,
              yourSeat: game.yourSeat,
              activeSeat: game.activeSeat,
            }),
          )
        },
        // A failed/timed-out check just means "couldn't tell, stay quiet" —
        // the next scheduled tick is the next attempt, same as PresenceRepository.
        failed: null,
      },
    )
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.gamesPm = []
    })
  }
}
